import { supabase } from '@/lib/supabase';
import type {
  Customer,
  CustomerListItem,
  CreateCustomerInput,
  UpdateCustomerInput,
} from '../types';
import type { CreateCustomerWithPhonesInput } from '../types';
import { addPhonesToCustomer } from './customerPhonesApi';

/**
 * Fetch all customers for a business using the v_customers_list view
 * Now includes all_phones and phone_count fields
 */
export async function fetchCustomers(
  businessId: string
): Promise<CustomerListItem[]> {
  const { data, error } = await supabase
    .from('v_customers_list')
    .select('*')
    .eq('business_id', businessId)
    .order('full_name', { ascending: true });

  if (error) {
    console.error('[customersApi] Error fetching customers:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Search customers by name, email, or phone
 */
export async function searchCustomers(
  businessId: string,
  searchTerm: string
): Promise<CustomerListItem[]> {
  const { data, error } = await supabase
    .from('v_customers_list')
    .select('*')
    .eq('business_id', businessId)
    .or(
      `full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`
    )
    .order('full_name', { ascending: true })
    .limit(20);

  if (error) {
    console.error('[customersApi] Error searching customers:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Fetch a single customer by ID with full details
 */
export async function fetchCustomerById(
  customerId: string
): Promise<CustomerListItem | null> {
  const { data, error } = await supabase
    .from('v_customers_list')
    .select('*')
    .eq('id', customerId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('[customersApi] Error fetching customer:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Check if a customer with the given email already exists
 */
export async function checkCustomerByEmail(
  businessId: string,
  email: string
): Promise<CustomerListItem | null> {
  const { data, error } = await supabase
    .from('v_customers_list')
    .select('*')
    .eq('business_id', businessId)
    .ilike('email', email.trim())
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[customersApi] Error checking customer email:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Create a new customer (without phones - use createCustomerWithPhones for full creation)
 */
export async function createCustomer(
  input: CreateCustomerInput
): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .insert({
      business_id: input.business_id,
      first_name: input.first_name,
      last_name: input.last_name,
      email: input.email,
      phone: input.phone || null,
      billing_street_address: input.billing_street_address,
      billing_unit: input.billing_unit || null,
      billing_city: input.billing_city,
      billing_province: input.billing_province,
      billing_postal_code: input.billing_postal_code,
      billing_country: input.billing_country,
    })
    .select()
    .single();

  if (error) {
    console.error('[customersApi] Error creating customer:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Create a new customer with multiple phones
 * This is the preferred method for creating customers with phone support
 */
export async function createCustomerWithPhones(
  input: CreateCustomerWithPhonesInput
): Promise<Customer> {
  // First create the customer without a phone
  // The phones will be added separately and the primary will sync
  const { data: customer, error } = await supabase
    .from('customers')
    .insert({
      business_id: input.business_id,
      first_name: input.first_name,
      last_name: input.last_name,
      email: input.email,
      phone: null, // Will be set by trigger when primary phone is added
      billing_street_address: input.billing_street_address,
      billing_unit: input.billing_unit || null,
      billing_city: input.billing_city,
      billing_province: input.billing_province,
      billing_postal_code: input.billing_postal_code,
      billing_country: input.billing_country,
    })
    .select()
    .single();

  if (error) {
    console.error('[customersApi] Error creating customer:', error);
    throw new Error(error.message);
  }

  // Now add the phones
  if (input.phones && input.phones.length > 0) {
    await addPhonesToCustomer(customer.id, input.phones);
  }

  return customer;
}

/**
 * Update an existing customer
 */
export async function updateCustomer(
  customerId: string,
  input: UpdateCustomerInput
): Promise<Customer> {
  // Don't allow direct phone updates - use phone API instead
  const { phone: _phone, ...updateFields } = input;

  const { data, error } = await supabase
    .from('customers')
    .update(updateFields)
    .eq('id', customerId)
    .select()
    .single();

  if (error) {
    console.error('[customersApi] Error updating customer:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Delete a customer (only if no projects reference it)
 */
export async function deleteCustomer(customerId: string): Promise<void> {
  const { error } = await supabase
    .from('customers')
    .delete()
    .eq('id', customerId);

  if (error) {
    console.error('[customersApi] Error deleting customer:', error);
    throw new Error(error.message);
  }
}
